import React, { useEffect, useState } from "react";
import firebase from "firebase/compat/app";
import { db } from "../../firebase";
import "./DashboardModal.scss";

interface DrawingMeta {
  id: string;
  title: string;
  updatedAt: string;
  folderId?: string | null;
}

interface FolderMeta {
  id: string;
  name: string;
  parentId?: string | null;
  createdAt: string;
}

interface DashboardModalProps {
  user: firebase.User;
  onClose: () => void;
  onLoadDrawing: (id: string, elements: any, appState: any) => void;
  onCreateNew: (folderId: string | null) => void;
}

export const DashboardModal: React.FC<DashboardModalProps> = ({ user, onClose, onLoadDrawing, onCreateNew }) => {
  const [allDrawings, setAllDrawings] = useState<DrawingMeta[]>([]);
  const [allFolders, setAllFolders] = useState<FolderMeta[]>([]);
  const [loadingDrawings, setLoadingDrawings] = useState(true);
  const [loadingFolders, setLoadingFolders] = useState(true);
  const [folderPath, setFolderPath] = useState<{id: string | null, name: string}[]>([{id: null, name: "My Workspace"}]);
  const currentFolderId = folderPath[folderPath.length - 1].id;
  const currentFolderName = folderPath[folderPath.length - 1].name;

  // Fetch drawings
  useEffect(() => {
    const unsubscribe = db.collection("drawings")
      .where("userId", "==", user.uid)
      // Removing orderBy so we don't strictly require a composite index if the user hasn't created it yet
      .onSnapshot((snapshot) => {
        const fetchedDrawings = snapshot.docs.map(doc => ({
          id: doc.id,
          title: doc.data().title || "Untitled Drawing",
          updatedAt: doc.data().updatedAt,
          folderId: doc.data().folderId || null,
        })).sort((a,b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        
        setAllDrawings(fetchedDrawings);
        setLoadingDrawings(false);
      }, (error) => {
        console.error("Error fetching drawings:", error);
        // Fallback
        db.collection("drawings").where("userId", "==", user.uid).get().then(snap => {
            const fetched = snap.docs.map(doc => ({
                id: doc.id,
                title: doc.data().title || "Untitled Drawing",
                updatedAt: doc.data().updatedAt,
                folderId: doc.data().folderId || null,
            })).sort((a,b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
            setAllDrawings(fetched);
            setLoadingDrawings(false);
        }).catch(() => setLoadingDrawings(false));
      });

    return () => unsubscribe();
  }, [user.uid]);

  // Fetch folders
  useEffect(() => {
    const unsubscribe = db.collection("folders")
      .where("userId", "==", user.uid)
      .onSnapshot((snapshot) => {
        const fetchedFolders = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name || "New Folder",
          parentId: doc.data().parentId || null,
          createdAt: doc.data().createdAt,
        })).sort((a,b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        
        setAllFolders(fetchedFolders);
        setLoadingFolders(false);
      }, (error) => {
        console.error("Error fetching folders:", error);
        db.collection("folders").where("userId", "==", user.uid).get().then(snap => {
            const fetched = snap.docs.map(doc => ({
                id: doc.id,
                name: doc.data().name || "New Folder",
                parentId: doc.data().parentId || null,
                createdAt: doc.data().createdAt,
            })).sort((a,b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
            setAllFolders(fetched);
            setLoadingFolders(false);
        }).catch(() => setLoadingFolders(false));
      });

    return () => unsubscribe();
  }, [user.uid]);

  const handleOpen = async (id: string) => {
    try {
      const doc = await db.collection("drawings").doc(id).get();
      if (doc.exists) {
        const data = doc.data();
        onLoadDrawing(id, data?.elements || [], data?.appState || {});
      }
    } catch (error) {
      console.error("Failed to load drawing", error);
      alert("Failed to load drawing");
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this drawing?")) {
      await db.collection("drawings").doc(id).delete();
    }
  };

  const handleRenameDrawing = async (e: React.MouseEvent, id: string, oldName: string) => {
    e.stopPropagation();
    const newName = window.prompt("Enter new drawing name:", oldName);
    if (newName && newName.trim() !== "" && newName !== oldName) {
      try {
        const doc = await db.collection("drawings").doc(id).get();
        if (doc.exists) {
          const appState = doc.data()?.appState || {};
          appState.name = newName.trim();
          await db.collection("drawings").doc(id).update({
            title: newName.trim(),
            appState: appState,
            updatedAt: new Date().toISOString()
          });
        }
      } catch (error) {
        console.error("Failed to rename drawing", error);
      }
    }
  };

  const handleCreateFolder = async () => {
    const folderName = window.prompt("Enter folder name:");
    if (folderName && folderName.trim() !== "") {
      try {
        await db.collection("folders").add({
          userId: user.uid,
          name: folderName.trim(),
          parentId: currentFolderId,
          createdAt: new Date().toISOString()
        });
      } catch (error) {
        console.error("Failed to create folder", error);
        alert("Failed to create folder");
      }
    }
  };

  const handleRenameFolder = async (e: React.MouseEvent, id: string, oldName: string) => {
    e.stopPropagation();
    const newName = window.prompt("Enter new folder name:", oldName);
    if (newName && newName.trim() !== "" && newName !== oldName) {
      try {
        await db.collection("folders").doc(id).update({
          name: newName.trim()
        });
      } catch (error) {
        console.error("Failed to rename folder", error);
      }
    }
  };

  const handleDeleteFolder = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this folder and ALL its sub-folders and drawings?")) {
      try {
        const getAllDescendantFolderIds = (folderId: string): string[] => {
          const children = allFolders.filter(f => f.parentId === folderId).map(f => f.id);
          let descendants = [...children];
          children.forEach(childId => {
            descendants = [...descendants, ...getAllDescendantFolderIds(childId)];
          });
          return descendants;
        };
        
        const folderIdsToDelete = [id, ...getAllDescendantFolderIds(id)];

        for (const fId of folderIdsToDelete) {
          await db.collection("folders").doc(fId).delete();
          const drawingsToDelete = allDrawings.filter(d => d.folderId === fId);
          for (const d of drawingsToDelete) {
            await db.collection("drawings").doc(d.id).delete();
          }
        }
      } catch (error) {
        console.error("Failed to delete folder", error);
      }
    }
  };

  const visibleDrawings = allDrawings.filter(d => (d.folderId || null) === currentFolderId);
  const visibleFolders = allFolders.filter(f => (f.parentId || null) === currentFolderId);

  const loading = loadingDrawings || loadingFolders;

  return (
    <div className="DashboardModal__overlay">
      <div className="DashboardModal__container">
        <header className="DashboardModal__header">
          <div className="DashboardModal__header-left">
            {folderPath.length > 1 && (
              <button className="DashboardModal__back-btn" onClick={() => setFolderPath(prev => prev.slice(0, -1))}>
                ← Back
              </button>
            )}
            <h2>{currentFolderName}</h2>
          </div>
          <button className="DashboardModal__close" onClick={onClose}>&times;</button>
        </header>

        <div className="DashboardModal__actions">
          <button className="DashboardModal__secondary-btn" onClick={handleCreateFolder}>
            + New Folder
          </button>
          <button className="DashboardModal__new-btn" onClick={() => onCreateNew(currentFolderId)}>
            + Create New Drawing
          </button>
        </div>

        {loading ? (
          <div className="DashboardModal__loading">Loading...</div>
        ) : (
          <div className="DashboardModal__grid">
            {/* Render Folders */}
            {visibleFolders.map((f) => (
              <div key={f.id} className="DashboardModal__card folder-card" onClick={() => setFolderPath(prev => [...prev, {id: f.id, name: f.name}])}>
                <div className="DashboardModal__card-thumbnail">
                  <div className="folder-icon">📁</div>
                </div>
                <div className="DashboardModal__card-info">
                  <span className="DashboardModal__card-title">{f.name}</span>
                  <span className="DashboardModal__card-date">
                    {visibleDrawings.length} Drawings
                  </span>
                </div>
                <div className="DashboardModal__card-actions">
                  <button className="DashboardModal__card-edit" onClick={(e) => handleRenameFolder(e, f.id, f.name)} title="Rename folder">
                    ✏️
                  </button>
                  <button className="DashboardModal__card-delete" onClick={(e) => handleDeleteFolder(e, f.id)} title="Delete folder">
                    &times;
                  </button>
                </div>
              </div>
            ))}

            {/* Render Drawings */}
            {visibleDrawings.map((d) => (
              <div key={d.id} className="DashboardModal__card drawing-card" onClick={() => handleOpen(d.id)}>
                <div className="DashboardModal__card-thumbnail">
                  <div className="DashboardModal__card-placeholder" />
                </div>
                <div className="DashboardModal__card-info">
                  <span className="DashboardModal__card-title">{d.title}</span>
                  <span className="DashboardModal__card-date">
                    {new Date(d.updatedAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="DashboardModal__card-actions">
                  <button className="DashboardModal__card-edit" onClick={(e) => handleRenameDrawing(e, d.id, d.title)} title="Rename drawing">
                    ✏️
                  </button>
                  <button className="DashboardModal__card-delete" onClick={(e) => handleDelete(e, d.id)} title="Delete drawing">
                    &times;
                  </button>
                </div>
              </div>
            ))}

            {visibleFolders.length === 0 && visibleDrawings.length === 0 && (
              <div className="DashboardModal__empty">This space is empty.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
